import React, { useState, useEffect } from 'react';
import {
    FileText, Megaphone, Facebook, Linkedin, Mail, Video as WebinarIcon, Instagram,
    Wand2, LayoutDashboard, Layers, PenSquare, Users, SearchCode, CheckCircle, XCircle
} from 'lucide-react';
import apiService from '../../services/apiService';
import Modal from '../../components/Modal';
import { DynamicCreatorForm, channelConfig } from './DynamicCreatorForm'; // Assuming you extract this to its own file

// --- Reusable UI Components ---
const Card = ({ children, className = '' }) => (
    <div className={`bg-slate-800 border border-slate-700 rounded-lg shadow-md ${className}`}>
        {children}
    </div>
);

const Button = ({ children, onClick, className = '', variant = 'primary', icon: Icon, disabled = false, ...props }) => {
    const baseClasses = 'flex items-center justify-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
    const variants = {
        primary: 'bg-brand-500 text-white hover:bg-brand-400',
        secondary: 'bg-slate-600 text-slate-100 hover:bg-slate-500',
        ghost: 'bg-transparent text-slate-400 hover:bg-slate-700/50 hover:text-slate-200',
        success: 'bg-emerald-600 text-white hover:bg-emerald-700'
    };
    return (
        <button onClick={onClick} disabled={disabled} className={`${baseClasses} ${variants[variant]} ${className}`} {...props}>
            {Icon && <Icon size={16} />}
            {children}
        </button>
    );
};

// --- Main Content Creator View ---
export default function ContentCreationView({ setActiveView, activeView, editingDraft, setEditingDraft }) {
    const [creatorType, setCreatorType] = useState('wordpress-blog');
    const [isPublishing, setIsPublishing] = useState(false);
    const [isOptimizing, setIsOptimizing] = useState(false); // --- CHANGE: State for optimization
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [formData, setFormData] = useState({});

    const contentTypeMap = {
        'wordpress-blog': 'Blog Post',
        'emails': 'Email',
        'webinars': 'Webinar',
        'facebook-post': 'Facebook Post',
        'linkedin-post': 'LinkedIn Post',
        'google-ad': 'Google Ad',
        'linkedin-ad': 'LinkedIn Ad',
        'facebook-ad': 'Facebook Ad',
        'instagram-ad': 'Instagram Ad',
    };

    useEffect(() => {
        if (editingDraft) {
            const draftCreatorType = Object.keys(contentTypeMap).find(key => contentTypeMap[key] === editingDraft.contentType);
            if (draftCreatorType) {
                setCreatorType(draftCreatorType);
            }
            try {
                // Safely parse content, fallback to an object
                const parsedContent = JSON.parse(editingDraft.content);
                setFormData({ title: editingDraft.title, ...parsedContent });
            } catch (e) {
                console.error("Could not parse draft content:", e);
                setFormData({ title: editingDraft.title });
            }
        } else {
            setFormData({}); // Clear form for new content
        }
    }, [editingDraft]);

    const handleFileUpload = async (file) => {
        try {
            const response = await apiService.uploadContentAsset(file);
            return response.filename; // Use the filename returned by the API
        } catch (error) {
            alert(`File upload failed: ${error.message}`);
            return null;
        }
    };

    // --- NEW: AI Optimization Handler ---
    const handleOptimizeWithAi = async () => {
        setIsOptimizing(true);
        try {
            const result = await apiService.optimizeContent({
                creatorType: creatorType,
                userInput: formData,
            });
            // The API now returns the full, optimized object
            setFormData(prev => ({...prev, ...result}));
        } catch (error) {
            alert(`AI Optimization Failed: ${error.message}`);
        } finally {
            setIsOptimizing(false);
        }
    };

    const handlePublishOrSave = async (status) => {
        setIsPublishing(true);
        const { title, ...contentFields } = formData;
        if (!title) {
            alert('Please provide a title before saving.');
            setIsPublishing(false);
            return;
        }
        const assetData = {
            title,
            content: JSON.stringify(contentFields),
            contentType: contentTypeMap[creatorType] || 'General',
            status: status
        };

        try {
            if (editingDraft?._id) {
                await apiService.updatePublishedAsset(editingDraft._id, assetData);
            } else {
                await apiService.publishAsset(assetData);
            }
            setShowSuccessModal(true);
        } catch (error) {
            alert(`Failed to save asset: ${error.message}`);
        } finally {
            setIsPublishing(false);
        }
    };

    const handleDiscard = () => {
        setEditingDraft(null);
        setFormData({});
        setCreatorType('wordpress-blog');
    };
    
    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        handleDiscard();
        setActiveView('foundry'); // Go back to the main dashboard
    };

    const navItems = [
        { id: 'foundry', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'campaigns', label: 'Campaign Builder', icon: Layers },
        { id: 'foundryContentCreation', label: 'Content Creator', icon: PenSquare },
        { id: 'foundryCompetitorAnalysis', label: 'Competitor Analysis', icon: Users },
        { id: 'seoAudit', label: 'SEO Audit', icon: SearchCode },
    ];

    const creatorTools = [
        { id: 'wordpress-blog', label: 'Blog Article', icon: FileText },
        { id: 'emails', label: 'Email', icon: Mail },
        { id: 'webinars', label: 'Webinar', icon: WebinarIcon },
        { id: 'google-ad', label: 'Google Ad', icon: Megaphone },
        { id: 'linkedin-ad', label: 'LinkedIn Ad', icon: Linkedin },
    ];

    return (
        <>
            <div className="p-6 animate-fade-in">
                <header className="flex flex-col md:flex-row justify-between md:items-center gap-4 border-b border-slate-700 pb-4 mb-6">
                    <h1 className="text-3xl font-bold text-white">Foundry</h1>
                    <div className="bg-slate-800 p-1 rounded-lg flex items-center gap-1 flex-wrap">
                        {navItems.map(item => (
                            <button 
                                key={item.id} 
                                onClick={() => setActiveView(item.id)} 
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors duration-200 ${activeView === item.id ? 'bg-brand-500/20 text-brand-300' : 'text-slate-300 hover:bg-slate-700'}`}
                            >
                                <item.icon size={16} />
                                {item.label}
                            </button>
                        ))}
                    </div>
                </header>
                <div className="flex flex-col md:flex-row gap-6">
                    <aside className="w-full md:w-60 md:flex-shrink-0">
                        <Card className="p-2">
                            <h3 className="text-lg font-bold text-white mb-4 p-2">Content Types</h3>
                            <div className="space-y-1">
                                {creatorTools.map(tool => (
                                    <button 
                                        key={tool.id} 
                                        onClick={() => {
                                            setCreatorType(tool.id);
                                            setFormData({});
                                        }} 
                                        className={`w-full flex items-center gap-3 p-3 text-left rounded-md transition-colors ${creatorType === tool.id ? 'bg-slate-700 text-white' : 'text-slate-300 hover:bg-slate-700/50'}`}
                                    >
                                        <tool.icon size={20} className={creatorType === tool.id ? 'text-brand-400' : 'text-slate-400'} />
                                        <span className="font-semibold">{tool.label}</span>
                                    </button>
                                ))}
                            </div>
                        </Card>
                    </aside>

                    <main className="flex-grow">
                        <Card className="!p-0 flex flex-col h-full">
                             <div className="p-4 flex justify-between items-center border-b border-slate-700">
                                <h2 className="text-xl font-bold text-white">
                                    {channelConfig[creatorType]?.title || 'Content Editor'}
                                </h2>
                                <Button icon={Wand2} variant="primary" onClick={handleOptimizeWithAi} disabled={isOptimizing}>
                                    {isOptimizing ? 'Optimizing...' : 'Optimize with AI'}
                                </Button>
                            </div>
                            <div className="flex-grow overflow-y-auto p-4">
                                <DynamicCreatorForm 
                                    channel={creatorType} 
                                    formData={formData}
                                    setFormData={setFormData}
                                    handleFileUpload={handleFileUpload}
                                />
                            </div>
                            <div className="p-4 flex justify-end items-center gap-3 border-t border-slate-700 mt-auto">
                                <Button variant="ghost" onClick={handleDiscard} disabled={isPublishing}>Discard</Button>
                                <Button variant="secondary" onClick={() => handlePublishOrSave('Draft')} disabled={isPublishing}>Save Draft</Button>
                                <Button variant="primary" onClick={() => handlePublishOrSave('Published')} disabled={isPublishing}>
                                    {isPublishing ? 'Publishing...' : 'Publish'}
                                </Button>
                            </div>
                        </Card>
                    </main>
                </div>
            </div>
            
            {showSuccessModal && (
                <Modal isOpen={true} onClose={handleCloseSuccessModal}>
                    <div className="text-center">
                        <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
                        <h3 className="mt-4 text-xl font-semibold text-white">Success!</h3>
                        <p className="mt-2 text-slate-300">Your content has been saved to the Cargo Bay.</p>
                        <div className="mt-6">
                            <Button onClick={handleCloseSuccessModal}>Return to Dashboard</Button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}